/**
 * Messages, app-wide (mounted once in SideNav for salon staff): the banner
 * for new messages, where tapping a notification goes, and keeping this
 * phone's push token with the server.
 */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ensureStarted, actions } from '../data/store';
import { addFallbackPeekHandler } from '../peek/MessagesHeaderButton';
import { canSeeSpace } from '../data/spaces';
import { listen, permission, turnOn } from './bridge';
import { pathForPush } from './pushRoutes';
import NewMessageBanner from './NewMessageBanner';

const MessagesPeek = lazy(() => import('../peek/MessagesPeek'));

let mounted = 0; // one at a time, however many places render it

export default function MessagesGlobal() {
    ensureStarted();
    const history = useHistory();
    const [first] = React.useState(() => { mounted += 1; return mounted === 1; });
    useEffect(() => () => { mounted -= 1; }, []);
    const [peek, setPeek] = useState(null); // { chatId, card }

    // "Message client" and "Ask the team" on pages with no Messages button in
    // the header (the diary's booking panel): open the quick peek here
    useEffect(() => {
        if (!first) return undefined;
        return addFallbackPeekHandler((d) => {
            const wide = window.innerWidth >= 768;
            if (d.client) {
                if (!canSeeSpace('clients')) return;
                actions.openClientChat(d.client).then((c) => {
                    if (!wide) history.push(`/messages/clients/${c.id}`);
                    else setPeek({ chatId: c.id, card: null });
                });
                return;
            }
            if (!wide) { history.push(d.conversationId ? `/messages/team/${d.conversationId}` : '/messages'); return; }
            setPeek({ chatId: d.conversationId || null, card: d.card || null });
        });
    }, [first, history]);

    useEffect(() => {
        if (!first) return undefined;
        const stop = listen({
            onToken: (token, platform) => { if (token) actions.registerDevice(token, platform); },
            onTap: (data) => history.push(pathForPush(data)),
        });
        // Already allowed on this phone: register again so the server has a fresh token
        permission().then((state) => { if (state === 'granted') turnOn(); }).catch(() => {});
        return stop;
    }, [first, history]);

    if (!first) return null;
    return (
        <>
            <NewMessageBanner history={history} />
            {peek && (
                <Suspense fallback={null}>
                    <MessagesPeek initialChatId={peek.chatId} initialCard={peek.card} onClose={() => setPeek(null)} />
                </Suspense>
            )}
        </>
    );
}
